import { getFormProps, getInputProps, SubmissionResult, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { QuoteSession } from '#app/utils/get-a-quote/quote.server.ts';
import { QuoteForm } from './quote-form';
import { useIsPending } from '#app/utils/misc.tsx';
import { Field } from '../forms';
import { StatusButton } from '../ui/status-button';
import { HoneypotInputs } from 'remix-utils/honeypot/react';

export const PersonalInfoSchema = z.object({
    firstName: z.string().nonempty({ message: 'First name is required' }),
    lastName: z.string().nonempty({ message: 'Last name is required' }),
});

export function PersonalInfo({ session }: { session: QuoteSession }) {
    const [lastResult, setLastResult] = useState<SubmissionResult<string[]> | null | undefined>()
    const { formData } = session;
    const [form, fields] = useForm({
        id: 'quote-personal-info',
        constraint: getZodConstraint(PersonalInfoSchema),
        lastResult: lastResult,
        defaultValue: {
            firstName: formData.firstName,
            lastName: formData.lastName,
        },
        onValidate({ formData }) {
            const submission = parseWithZod(formData, { schema: PersonalInfoSchema });
            return submission;
        }
    })

    return (
        <div className='container'>
            <div className='py-5 lg:py-10'>
                <h2 className='text-center max-w-[720px] mx-auto'>Step 1: Your Name</h2>
            </div>
            <QuoteForm
                action={QuoteForm.ACTIONS.NextStep}
                formProps={{...getFormProps(form)}}
                className="max-w-80 mx-auto"
            >
                {(fetcher) => {
                    const fetchedData = fetcher.data;
                    if (fetchedData !== lastResult) {
                        setLastResult(fetchedData);
                    }

                    return (
                        <>
                            <HoneypotInputs />
                            <Field
                                labelProps={{ children: 'First Name' }}
                                inputProps={{
                                    ...getInputProps(fields.firstName, { type: 'text', required: true }),
                                    autoFocus: true,
                                    autoComplete: 'given-name',
                                }}
                                errors={fields.firstName.errors}
                            />
                            <Field
                                labelProps={{ children: 'Last Name' }}
                                inputProps={{
                                    ...getInputProps(fields.lastName, { type: 'text', required: true }),
                                    autoComplete: 'family-name',
                                }}
                                errors={fields.lastName.errors}
                            />
                            <StatusButton
                                type="submit"
                                status={fetcher.state !== 'idle' ? 'pending' : (form.status ?? 'idle')}
                                disabled={fetcher.state !== 'idle'}
                                className='mx-auto'
                            >
                                Next
                            </StatusButton>
                        </>
                    );
                }}
            </QuoteForm>
        </div>
    )
}